
import React, { useState, useEffect, useMemo } from "react";
import { useLeads, Lead } from "@/context/LeadContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  MessageSquare,
  Send,
  Phone,
  BarChart2,
  Users,
  RefreshCw,
  Sparkles,
  FileText,
  Zap,
  Eye,
  Search,
  AlertCircle,
  CheckCircle2,
  Clock3
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import SMSTracker from "@/components/messages/SMSTracker";
import UnassignedLeadNotifier from "@/components/messages/UnassignedLeadNotifier";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MessagesContent = () => {
  const { leads, sendSMS, refreshLeads } = useLeads();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedLeadId, setSelectedLeadId] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("unassigned");
  const [refreshing, setRefreshing] = useState(false);
  const [monitorSearch, setMonitorSearch] = useState("");
  const [monitorStatusFilter, setMonitorStatusFilter] = useState<string>("all");
  const [monitorServiceFilter, setMonitorServiceFilter] = useState<string>("all");
  const [monitorPartnerFilter, setMonitorPartnerFilter] = useState<string>("all");
  const [templates] = useState<string[]>([
    "Καλησπέρα σας! Το αίτημά σας έχει ληφθεί και θα επικοινωνήσουμε σύντομα μαζί σας.",
    "Σας ευχαριστούμε για το ενδιαφέρον σας. Ένας εκπρόσωπός μας θα επικοινωνήσει μαζί σας εντός 24 ωρών.",
    "Σας ενημερώνουμε ότι το αίτημά σας προωθήθηκε στο τμήμα εξυπηρέτησης πελατών."
  ]);

  const selectedLead = leads.find(lead => lead.id === selectedLeadId);

  const handleRefreshData = async () => {
    try {
      setRefreshing(true);
      await refreshLeads();
      toast({
        title: "Επιτυχής ανανέωση",
        description: "Τα δεδομένα ενημερώθηκαν επιτυχώς",
      });
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast({
        title: "Σφάλμα ανανέωσης",
        description: "Υπήρξε πρόβλημα κατά την ανανέωση των δεδομένων",
        variant: "destructive"
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSend = async () => {
    if (!selectedLead || !message.trim()) {
      toast({
        title: "Σφάλμα",
        description: "Παρακαλώ επιλέξτε πελάτη και συμπληρώστε το μήνυμα",
        variant: "destructive"
      });
      return;
    }

    try {
      setSending(true);
      await sendSMS(selectedLead.id, message, selectedLead.phone);

      toast({
        title: "Επιτυχία",
        description: "Το μήνυμα στάλθηκε επιτυχώς",
      });

      setMessage("");
    } catch (error) {
      console.error("Error sending SMS:", error);
      toast({
        title: "Σφάλμα αποστολής",
        description: "Υπήρξε πρόβλημα κατά την αποστολή του μηνύματος",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleNotificationSuccess = () => {
    refreshLeads();
  };

  const useTemplate = (template: string) => {
    let personalizedMessage = template;
    if (selectedLead?.name) {
      personalizedMessage = personalizedMessage.replace(
        "Καλησπέρα σας!",
        `Καλησπέρα σας ${selectedLead.name}!`
      );
    }
    setMessage(personalizedMessage);
  };

  const validLeads = leads.filter(lead => lead.phone && lead.phone.trim() !== "");
  const unassignedLeadsCount = leads.filter(lead => !lead.partnerId).length;
  const smsSentCount = leads.filter(lead => lead.smsStatus === "sent" || lead.smsStatus === "delivered").length;
  const viewedCount = leads.filter(lead => lead.viewed).length;

  const serviceOptions = useMemo(() => {
    return Array.from(new Set(leads.map(lead => lead.service).filter(Boolean))).sort();
  }, [leads]);

  const partnerOptions = useMemo(() => {
    return Array.from(new Set(leads.map(lead => lead.partnerName).filter(Boolean))).sort();
  }, [leads]);

  const deriveLeadStatus = (lead: Lead): "failed" | "viewed" | "delivered" | "sent" | "pending" => {
    if (lead.smsStatus === "failed") return "failed";
    if (lead.viewed) return "viewed";
    if (lead.smsStatus === "delivered") return "delivered";
    if (lead.smsStatus === "sent") return "sent";
    return "pending";
  };

  const trackedLeads = useMemo(() => {
    return leads.filter(lead => lead.smsStatus || lead.viewed || lead.partnerId);
  }, [leads]);

  const monitorStats = useMemo(() => {
    const totalTracked = trackedLeads.length;
    const sent = trackedLeads.filter(l => l.smsStatus === "sent").length;
    const delivered = trackedLeads.filter(l => l.smsStatus === "delivered").length;
    const failed = trackedLeads.filter(l => l.smsStatus === "failed").length;
    const viewed = trackedLeads.filter(l => l.viewed).length;
    const attempted = sent + delivered + failed;

    return {
      totalTracked,
      sent,
      delivered,
      failed,
      viewed,
      deliveryRate: attempted > 0 ? Math.round(((sent + delivered) / attempted) * 100) : 0,
      readRate: (sent + delivered) > 0 ? Math.round((viewed / (sent + delivered)) * 100) : 0,
    };
  }, [trackedLeads]);

  const filteredTrackedLeads = useMemo(() => {
    return trackedLeads.filter((lead) => {
      const status = deriveLeadStatus(lead);
      if (monitorStatusFilter !== "all" && status !== monitorStatusFilter) return false;
      if (monitorServiceFilter !== "all" && lead.service !== monitorServiceFilter) return false;
      if (monitorPartnerFilter !== "all" && lead.partnerName !== monitorPartnerFilter) return false;

      if (monitorSearch.trim()) {
        const q = monitorSearch.toLowerCase();
        const composite = `${lead.name} ${lead.phone} ${lead.address} ${lead.partnerName ?? ""}`.toLowerCase();
        if (!composite.includes(q)) return false;
      }

      return true;
    }).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [trackedLeads, monitorStatusFilter, monitorServiceFilter, monitorPartnerFilter, monitorSearch]);

  const followUpLeads = useMemo(() => {
    return trackedLeads
      .filter((lead) => (lead.smsStatus === "sent" || lead.smsStatus === "delivered") && !lead.viewed)
      .map((lead) => {
        const hoursSinceUpdate = Math.floor((Date.now() - new Date(lead.updatedAt).getTime()) / (1000 * 60 * 60));
        return { lead, hoursSinceUpdate };
      })
      .filter(({ hoursSinceUpdate }) => hoursSinceUpdate >= 12)
      .sort((a, b) => b.hoursSinceUpdate - a.hoursSinceUpdate)
      .slice(0, 8);
  }, [trackedLeads]);

  const goToStatistics = () => {
    navigate("/admin/messages/statistics");
  };

  const goToLeadDetails = (leadId: string) => {
    navigate(`/admin/lead/${leadId}`);
  };

  const quickComposeForLead = (lead: Lead) => {
    setSelectedLeadId(lead.id);
    setMessage(`Καλησπέρα σας ${lead.name}, θα επικοινωνήσουμε άμεσα μαζί σας για το αίτημά σας.`);
    setActiveTab("compose");
  };

  const getStatusBadge = (lead: Lead) => {
    const status = deriveLeadStatus(lead);

    if (status === "viewed") {
      return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Αναγνώστηκε</Badge>;
    }
    if (status === "delivered") {
      return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Παραδόθηκε</Badge>;
    }
    if (status === "sent") {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Απεστάλη</Badge>;
    }
    if (status === "failed") {
      return <Badge variant="destructive">Αποτυχία</Badge>;
    }
    return <Badge variant="outline">Εκκρεμεί</Badge>;
  };

  useEffect(() => {
    refreshLeads();
  }, []);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg">
            <MessageSquare className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Μηνύματα</h1>
            <p className="text-slate-500 mt-0.5">Αποστολή SMS και ειδοποιήσεις</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleRefreshData}
            variant="outline"
            disabled={refreshing}
            className="border-slate-200"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Ανανέωση
          </Button>
          <Button onClick={goToStatistics} variant="outline" className="border-slate-200">
            <BarChart2 className="h-4 w-4 mr-2" />
            Στατιστικά
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 bg-gradient-to-br from-violet-50 to-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-violet-600">{unassignedLeadsCount}</p>
                <p className="text-sm text-slate-600 font-medium">Μη Ανατεθειμένα</p>
              </div>
              <div className="p-2 rounded-lg bg-violet-100">
                <Users className="h-5 w-5 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">{smsSentCount}</p>
                <p className="text-sm text-slate-600 font-medium">SMS Απεσταλμένα</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-100">
                <Send className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-blue-600">{validLeads.length}</p>
                <p className="text-sm text-slate-600 font-medium">Διαθέσιμες Επαφές</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100">
                <Phone className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-gradient-to-br from-cyan-50 to-sky-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-cyan-600">{viewedCount}</p>
                <p className="text-sm text-slate-600 font-medium">Αναγνώσεις</p>
              </div>
              <div className="p-2 rounded-lg bg-cyan-100">
                <Eye className="h-5 w-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="unassigned" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Users className="h-4 w-4 mr-2" />
            Μη Ανατεθειμένοι
            {unassignedLeadsCount > 0 && (
              <Badge variant="secondary" className="ml-2 bg-violet-100 text-violet-700">
                {unassignedLeadsCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="compose" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <MessageSquare className="h-4 w-4 mr-2" />
            Αποστολή SMS
          </TabsTrigger>
          <TabsTrigger value="monitor" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Zap className="h-4 w-4 mr-2" />
            Monitoring
          </TabsTrigger>
        </TabsList>

        <TabsContent value="unassigned" className="mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <UnassignedLeadNotifier
                leads={leads}
                onSuccess={handleNotificationSuccess}
              />
            </div>

            <div>
              <SMSTracker leads={leads} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="compose" className="mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              {/* Compose Card */}
              <Card className="border-slate-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-violet-50">
                      <Send className="h-4 w-4 text-violet-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Σύνθεση Μηνύματος</CardTitle>
                      <CardDescription>Επιλέξτε παραλήπτη και γράψτε το μήνυμά σας</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Παραλήπτης</label>
                    <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
                      <SelectTrigger className="bg-slate-50 border-slate-200">
                        <SelectValue placeholder="Επιλέξτε παραλήπτη..." />
                      </SelectTrigger>
                      <SelectContent>
                        {validLeads.map((lead) => (
                          <SelectItem key={lead.id} value={lead.id}>
                            {lead.name} ({lead.phone})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedLead && (
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                          <span className="text-white font-semibold">{selectedLead.name.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{selectedLead.name}</p>
                          <p className="text-sm text-slate-500">{selectedLead.phone}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Μήνυμα</label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Γράψτε το μήνυμά σας..."
                      rows={5}
                      className="bg-slate-50 border-slate-200 resize-none"
                    />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Χρησιμοποιήστε τα πρότυπα για γρήγορη σύνθεση</span>
                      <span className={message.length > 160 ? "text-amber-600" : ""}>
                        {message.length}/160 χαρακτήρες
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleSend}
                    disabled={sending || !selectedLeadId || !message.trim()}
                    className="w-full btn-gradient h-11"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {sending ? "Αποστολή..." : "Αποστολή SMS"}
                  </Button>
                </CardContent>
              </Card>

              {/* Templates Card */}
              <Card className="border-slate-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-amber-50">
                      <FileText className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Πρότυπα Μηνυμάτων</CardTitle>
                      <CardDescription>Επιλέξτε ένα έτοιμο πρότυπο</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {templates.map((template, index) => (
                      <div
                        key={index}
                        className="p-4 border border-slate-200 rounded-xl hover:border-violet-300 hover:bg-violet-50/50 cursor-pointer transition-all group"
                        onClick={() => useTemplate(template)}
                      >
                        <p className="text-sm text-slate-600 group-hover:text-slate-900">{template}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 text-violet-600 hover:text-violet-700 hover:bg-violet-100 p-0 h-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            useTemplate(template);
                          }}
                        >
                          <Sparkles className="h-3 w-3 mr-1" />
                          Χρήση προτύπου
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <SMSTracker leads={leads} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="monitor" className="mt-6 space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-4 w-4 text-slate-500" />
                Φίλτρα Παρακολούθησης
              </CardTitle>
              <CardDescription>Γρήγορη διάγνωση αποστολών και αναγνώσεων</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <Input
                placeholder="Αναζήτηση ονόματος, τηλεφώνου ή διεύθυνσης..."
                value={monitorSearch}
                onChange={(e) => setMonitorSearch(e.target.value)}
                className="bg-slate-50 border-slate-200"
              />

              <Select value={monitorStatusFilter} onValueChange={setMonitorStatusFilter}>
                <SelectTrigger className="bg-slate-50 border-slate-200">
                  <SelectValue placeholder="Κατάσταση" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες οι καταστάσεις</SelectItem>
                  <SelectItem value="viewed">Αναγνώστηκε</SelectItem>
                  <SelectItem value="delivered">Παραδόθηκε</SelectItem>
                  <SelectItem value="sent">Απεστάλη</SelectItem>
                  <SelectItem value="failed">Αποτυχία</SelectItem>
                  <SelectItem value="pending">Εκκρεμεί</SelectItem>
                </SelectContent>
              </Select>

              <Select value={monitorServiceFilter} onValueChange={setMonitorServiceFilter}>
                <SelectTrigger className="bg-slate-50 border-slate-200">
                  <SelectValue placeholder="Υπηρεσία" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες οι υπηρεσίες</SelectItem>
                  {serviceOptions.map((service) => (
                    <SelectItem key={service} value={service}>{service}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={monitorPartnerFilter} onValueChange={setMonitorPartnerFilter}>
                <SelectTrigger className="bg-slate-50 border-slate-200">
                  <SelectValue placeholder="Συνεργάτης" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλοι οι συνεργάτες</SelectItem>
                  {partnerOptions.map((partner) => (
                    <SelectItem key={partner} value={partner}>{partner}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <Card className="border-slate-200 bg-gradient-to-br from-indigo-50 to-indigo-100/40">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Tracked Leads</p>
                <p className="text-2xl font-bold text-indigo-700">{monitorStats.totalTracked}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-emerald-100/40">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Delivery Rate</p>
                <p className="text-2xl font-bold text-emerald-700">{monitorStats.deliveryRate}%</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-gradient-to-br from-cyan-50 to-cyan-100/40">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Read Rate</p>
                <p className="text-2xl font-bold text-cyan-700">{monitorStats.readRate}%</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-gradient-to-br from-rose-50 to-rose-100/40">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Αποτυχίες</p>
                <p className="text-2xl font-bold text-rose-700">{monitorStats.failed}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <Card className="border-slate-200 xl:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock3 className="h-4 w-4 text-slate-500" />
                  Timeline Μηνυμάτων
                </CardTitle>
                <CardDescription>
                  {filteredTrackedLeads.length} αποτελέσματα με βάση τα φίλτρα
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filteredTrackedLeads.length === 0 ? (
                  <div className="text-sm text-slate-500 py-8 text-center">Δεν υπάρχουν εγγραφές για τα φίλτρα που έβαλες.</div>
                ) : (
                  <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                    {filteredTrackedLeads.slice(0, 60).map((lead) => (
                      <div key={lead.id} className="p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div>
                            <p className="font-medium text-slate-900">{lead.name}</p>
                            <p className="text-xs text-slate-500">{lead.phone} • {lead.address}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              Συνεργάτης: {lead.partnerName || "Μη ανατεθειμένος"} • {new Date(lead.updatedAt).toLocaleString("el-GR")}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {getStatusBadge(lead)}
                            <Button size="sm" variant="outline" onClick={() => quickComposeForLead(lead)}>Quick SMS</Button>
                            <Button size="sm" onClick={() => goToLeadDetails(lead.id)}>Προβολή</Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  Follow-up Alerts
                </CardTitle>
                <CardDescription>Μηνύματα που στάλθηκαν αλλά δεν διαβάστηκαν ≥ 12 ώρες</CardDescription>
              </CardHeader>
              <CardContent>
                {followUpLeads.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">Δεν υπάρχουν εκκρεμή follow-ups.</div>
                ) : (
                  <div className="space-y-3">
                    {followUpLeads.map(({ lead, hoursSinceUpdate }) => (
                      <div key={lead.id} className="p-3 rounded-lg border border-amber-200 bg-amber-50/60">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{lead.name}</p>
                            <p className="text-xs text-slate-600 mt-0.5">{lead.partnerName || "Χωρίς συνεργάτη"}</p>
                          </div>
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-100">{hoursSinceUpdate}h</Badge>
                        </div>
                        <div className="mt-2 flex gap-2">
                          <Button size="sm" variant="outline" className="h-8" onClick={() => quickComposeForLead(lead)}>
                            <Send className="h-3 w-3 mr-1" /> Υπενθύμιση
                          </Button>
                          <Button size="sm" className="h-8" onClick={() => goToLeadDetails(lead.id)}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Άνοιγμα
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Messages = () => (
  <MessagesContent />
);

export default Messages;
